<template>
  <div class="tooltip-wrapper">
    <div class="tooltip-wrapper__hoverable-element" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave" ref="domRefHoverableElem">
      <slot name="hoverableElement" :isTooltipVisible="isTooltipVisible" />
    </div>

    <Teleport to="body">
      <div
        :class="{
          'tooltip-wrapper__tooltip-itself': true,
          'tooltip-wrapper__tooltip-itself--invisible': !isTooltipVisible,
        }"
        @mouseenter="handleTooltipMouseEnter"
        @mouseleave="handleTooltipMouseLeave"
        ref="domRefTooltip"
        :style="{
          width: props.width === 'auto' ? 'auto' : `${props.width}px`,
          maxWidth: '100dvw',
          ...tooltipPositionStyles,
        }"
      >
        <div
          :class="{
            'tooltip-wrapper__arrow': true,

            'tooltip-wrapper__arrow--top': tooltipPlacement === 'bottom',
            'tooltip-wrapper__arrow--right': tooltipPlacement === 'left',
            'tooltip-wrapper__arrow--bottom': tooltipPlacement === 'top',
            'tooltip-wrapper__arrow--left': tooltipPlacement === 'right',

            'tooltip-wrapper__arrow-bottom-left': tooltipPlacement === 'top-start',
            'tooltip-wrapper__arrow-top-left': tooltipPlacement === 'bottom-start',

            'tooltip-wrapper__arrow-bottom-right': tooltipPlacement === 'top-end',
            'tooltip-wrapper__arrow-top-right': tooltipPlacement === 'bottom-end',
          }"
          ref="domRefArrow"
          :style="{ transform: `translateX(${arrowLeftShift}px) rotate(45deg)` }"
        ></div>

        <slot name="tooltip" :isTooltipVisible="isTooltipVisible">
          {{ text }}
        </slot>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computePosition, type ComputePositionConfig, type Placement, shift, flip, offset as floatingUiOffset } from "@floating-ui/dom";
import { ref, onMounted, watch, nextTick } from "vue";

const props = withDefaults(
  defineProps<{
    text?: string;
    offset?: number;
    width?: number | "auto";
    placement?: Placement;
    transitionTime?: number;
  }>(),
  {
    text: "",
    offset: 10,
    width: "auto",
    placement: "top",
    transitionTime: 200,
  }
);

// Toggle tooltip =============================================================
const isTooltipVisible = ref<boolean>(false);

let showTimeout = null;
let hideTimeout = null;

function handleMouseEnter() {
  computeTooltipPosition();

  clearTimeout(showTimeout);
  clearTimeout(hideTimeout);
  showTimeout = setTimeout(() => (isTooltipVisible.value = true), 100);
}
function handleMouseLeave() {
  clearTimeout(showTimeout);
  clearTimeout(hideTimeout);
  hideTimeout = setTimeout(() => (isTooltipVisible.value = false), 100);
}

function handleTooltipMouseEnter() {
  clearTimeout(showTimeout);
  clearTimeout(hideTimeout);
}
function handleTooltipMouseLeave() {
  hideTimeout = setTimeout(() => (isTooltipVisible.value = false), 100);
}

// Set tooltip position =======================================================
const domRefHoverableElem = ref(null);
const domRefTooltip = ref(null);
const tooltipPositionStyles = ref({});
const tooltipArrowPositionStyles = ref({});

const tooltipPlacement = ref<Placement>("top");

onMounted(computeTooltipPosition);

function computeTooltipPosition(): void {
  const floatingUiOptions: ComputePositionConfig = {
    middleware: [floatingUiOffset(props.offset), flip(), shift()],
    placement: props.placement,
  };

  computePosition(domRefHoverableElem.value, domRefTooltip.value, floatingUiOptions).then(position => {
    tooltipPlacement.value = position.placement;

    tooltipPositionStyles.value = {
      top: `${position.y}px`,
      left: `${position.x}px`,
    };
    tooltipArrowPositionStyles.value = {
      x: position.middlewareData?.arrow?.x,
      y: position.middlewareData?.arrow?.y,
    };
  });
}

// Adjust the triangle horizontal position ====================================
const domRefArrow = ref(null);
const arrowLeftShift = ref<number>(0);
let arrowShiftTimeout = null;

watch(isTooltipVisible, async () => {
  clearTimeout(arrowShiftTimeout);
  arrowLeftShift.value = 0;

  if (tooltipPlacement.value.includes("top") || tooltipPlacement.value.includes("bottom")) {
    if (isTooltipVisible.value) {
      await nextTick();

      const hoverableElemLeft = domRefHoverableElem.value.getBoundingClientRect().left;
      const arrowLeft = domRefArrow.value.getBoundingClientRect().left;

      arrowLeftShift.value = hoverableElemLeft - arrowLeft;
    } else {
      arrowShiftTimeout = setTimeout(() => (arrowLeftShift.value = 0), props.transitionTime);
    }
  }
});
</script>

<style scoped lang="scss">
@import "@/scss/z-indexes.scss";

// Tooltip wrapper ============================================================
.tooltip-wrapper {
  display: inline-flex;
  position: relative;

  &__hoverable-element {
  }

  &__tooltip-itself {
    white-space: pre-wrap;
    padding: 10px 15px 12px;
    border-radius: 5px;
    position: absolute;
    z-index: $z-index-tooltip;
    color: #5b5b5b;
    font: 14px/18px sans-serif;
    background: #fff;
    opacity: 1;
    box-shadow: 0 5px 35px -9px rgba(0, 0, 0, 0.4);
    transition: opacity 0.09s ease-in-out;

    &--invisible {
      opacity: 0;
      pointer-events: none;
    }
  }

  &__arrow {
    width: 14px;
    height: 14px;
    position: absolute;
    z-index: -1;
    transform: rotate(45deg);
    background: #fff;

    &--top {
      inset: -4px auto auto calc(50% - 7px);
    }
    &--right {
      inset: calc(50% - 7px) -4px auto auto;
    }
    &--bottom {
      inset: auto auto -4px calc(50% - 7px);
    }
    &--left {
      inset: calc(50% - 7px) auto auto -4px;
    }

    &--bottom-left {
      inset: auto auto -4px 4px;
    }
    &--top-left {
      inset: -4px auto auto 4px;
    }

    &--bottom-right {
      inset: auto auto -4px calc(100% - 17px);
    }
    &--top-right {
      inset: -4px auto auto calc(100% - 17px);
    }
  }
}
</style>
